import { Maybe, MediaItem, MediaSize, Product as ProductResult, ProductsQuery, TermNode } from '.codegen/generatedApi'
import { GENERIC_IMG_NAME } from 'config'
import { asNonNullishArray, asNumber, isTaxonomyId } from 'helpers'
import { Product, TaxonomyId, Term, TermBase, TermIdentifier } from 'types'

/**
 * Convert the 'Yes/No' names/slugs of boolean
 * @param name
 * @returns
 */
export const parseBooleanTerm = <D extends true | false | undefined>(
  name: string | null | undefined | boolean | number,
  defaultValue: D
): boolean | D => {
  if (typeof name === 'number') return name ? true : false
  if (typeof name === 'boolean') return name

  name = name?.toUpperCase()

  return name === 'YES' ? true : name === 'TRUE' ? true : name === 'NO' ? false : name === 'FALSE' ? false : defaultValue
}

export const selectQueryTotal = (response: ProductsQuery): number | undefined => {
  return response.products?.pageInfo?.offsetPagination?.total ?? undefined
}

export const parseProductsQuery = (response: ProductsQuery, defaultProps?: Partial<Product>, forceProps?: Partial<Product>) => {
  const { products } = response

  return (products?.nodes ?? []).reduce((results, node) => {
    const product = {
      ...defaultProps,
      ...parseProductResult(node as ProductResult),
      ...forceProps,
    }
    return product.slug ? results.concat(product) : results
  }, [] as Product[])
}

export const parseImgMedia = (maybeMedia: Partial<MediaItem> | string | null | undefined): ParsedImgMedia => {
  const media: Partial<MediaItem> =
    typeof maybeMedia === 'string'
      ? {
          mediaItemUrl: maybeMedia,
        }
      : maybeMedia || {}

  const altText = media?.altText || ''
  const url = media?.mediaItemUrl || null
  const details = media?.mediaDetails
  const height = details?.height || 0
  const width = details?.width || 0
  const sizes = asNonNullishArray(details?.sizes)

  // if (!width || !height || !sizes.length) return null;

  return {
    altText,
    url,
    width,
    height,
    sizes,
  }
}

export type ParsedImgMedia = {
  altText: string
  url: string | null
  width: number
  height: number
  sizes: MediaSize[]
}

export const parseProductResult = <P extends Maybe<Partial<ProductResult>>>(node?: P): Product => {
  const selectTermBases = <T extends Maybe<TermNode>>(taxonomyId: TaxonomyId, nodes?: Maybe<T[]>) => {
    return (nodes || []).reduce(
      (l, v) =>
        v?.databaseId
          ? l.concat({
              id: v.databaseId,
              name: v.name || '',
              taxonomyId,
            })
          : l,
      [] as TermBase[]
    )
  }

  const pSettings = node?.productSettings

  const productCategories = selectTermBases('PRODUCTCATEGORY', node?.productCategories?.nodes)
  const mainCategory = productCategories.length ? productCategories[0] : undefined
  const sticker = pSettings && pSettings.sticker ? pSettings.sticker : ''
  const stickerImage = pSettings && pSettings.stickerimage ? pSettings.stickerimage : ''
  const maybeFeaturedImage = parseImgMedia(node?.featuredImage?.node)
  const packShotsString = pSettings && pSettings.packShots ? pSettings.packShots : ''

  // Get packshots, but filter out any "generic" images
  const packShots = packShotsString
    .split(',')
    .filter((img) => !img.includes(GENERIC_IMG_NAME))
    .map(parseImgMedia)

  const additionalImages = (node?.productSettings?.additionalImages || []).reduce((results, item) => {
    const img = parseImgMedia(item)
    return img ? results.concat(img) : results
  }, [] as ParsedImgMedia[])

  const images = [...(maybeFeaturedImage.url ? [maybeFeaturedImage] : []), ...packShots, ...additionalImages]

  const featuredImage = images.length ? images[0] : undefined

  const packQty = pSettings && pSettings.packQty && !isNaN(Number(pSettings.packQty)) ? Number(pSettings.packQty) : 0
  const packUnit = pSettings && pSettings.packUnit ? pSettings.packUnit : ''
  
  const packSize = packQty ? `${packQty} ${packUnit}` : undefined

  const brands = selectTermBases('BRAND', node?.brands?.nodes)
  const brand = concatTerms(brands)

  return {
    id: node?.databaseId ?? 0,
    title: node?.title ?? '',
    slug: node?.slug ?? '',
    content: node?.content ?? '',
    featuredImage,
    images,
    brand,
    packShots,
    packQty,
    packUnit,
    sticker,
    stickerImage,
    packSize,
    mainCategory,
    brands,
    stockists: selectTermBases('STOCKIST', node?.stockists?.nodes),
    subbrands: selectTermBases('SUBBRAND', node?.subbrands?.nodes),
    organics: selectTermBases('ORGANIC', node?.organics?.nodes),
    producingCountries: selectTermBases('PRODUCINGCOUNTRY', node?.producingCountries?.nodes),
    countries: selectTermBases('COUNTRY', node?.countries?.nodes),
    productCategories,
  }
}

export const concatTerms = (terms: TermBase[], joinStr: string = ', ') =>
  terms.map((v) => `${v.name}`).join(joinStr)

export type ParsedProduct = ReturnType<typeof parseProductResult>

export const isTermIdentifier = (x: Partial<Term>): x is TermIdentifier => {
  return asNumber(x.id) && x.taxonomyId && isTaxonomyId(x.taxonomyId)
    ? true
    : false
}

export const isProductFull = (x?: Product): x is Product<'full'> => {
  return x && x.status === 'full' ? true : false
}
